import type {
  Payment,
  PaymentCurrency,
  PaymentTypeKey,
  RawPaymentStatus,
  UiPaymentStatus,
} from '@models/payments';
import {
  BrandCodes,
  CollectionTypes,
  PaymentProviders,
  RawPaymentStatuses,
  UiPaymentStatuses,
} from '@models/payments';
import type { DashboardPaymentStatusFilter, DashboardPaymentTypeFilter } from './api';
import { PAYMENT_TYPE_LABELS } from './constants';
import { formatMaskedCardDigits, formatShortDate, formatShortTime } from '@shared/utils';

const PAYMENT_TYPE_TO_API: Record<PaymentTypeKey, DashboardPaymentTypeFilter> = {
  scheduledPayment: 'standard',
  debtCollection: 'debt_collection',
  subscriptionRecovery: 'recurring_recovery',
};

export function paymentTypeKeyToApi(key: PaymentTypeKey): DashboardPaymentTypeFilter {
  return PAYMENT_TYPE_TO_API[key];
}

const PAYMENT_STATUS_TO_API: Record<UiPaymentStatus, DashboardPaymentStatusFilter> = {
  [UiPaymentStatuses.SUCCEEDED]: 'completed',
  [UiPaymentStatuses.PENDING]: 'pending',
  [UiPaymentStatuses.FAILED]: 'failed',
  [UiPaymentStatuses.REFUNDED]: 'refunded',
  // the API has no partial-refund bucket; partially refunded payments live in its 'completed' bucket
  [UiPaymentStatuses.PARTIALLY_REFUNDED]: 'completed',
};

export function uiPaymentStatusToApi(status: UiPaymentStatus): DashboardPaymentStatusFilter {
  return PAYMENT_STATUS_TO_API[status];
}

export function formatPaymentDate(d: Date): { date: string; time: string } {
  return {
    date: formatShortDate(d),
    time: formatShortTime(d),
  };
}

export function formatPaymentAmount(
  minorUnits: number,
  currency: PaymentCurrency,
  decimalDigits = 2,
): string {
  const major = minorUnits / Math.pow(10, decimalDigits);
  return (
    currency.symbol +
    major.toLocaleString('en-US', {
      minimumFractionDigits: decimalDigits,
      maximumFractionDigits: decimalDigits,
    })
  );
}

export function getPaymentTypeKey(row: Payment): PaymentTypeKey {
  if (row.collectionType === CollectionTypes.DEBT_COLLECTION) return 'debtCollection';
  if (row.collectionType === CollectionTypes.SUBSCRIPTION_RECOVERY) return 'subscriptionRecovery';
  return 'scheduledPayment';
}

export function getPaymentTypeLabel(row: Payment): string {
  return PAYMENT_TYPE_LABELS[getPaymentTypeKey(row)];
}

const RAW_TO_UI_STATUS: Partial<Record<RawPaymentStatus, UiPaymentStatus>> = {
  [RawPaymentStatuses.COMPLETED]: UiPaymentStatuses.SUCCEEDED,
  [RawPaymentStatuses.SUCCEEDED]: UiPaymentStatuses.SUCCEEDED,
  [RawPaymentStatuses.SETTLED]: UiPaymentStatuses.SUCCEEDED,
  [RawPaymentStatuses.AUTHORIZED]: UiPaymentStatuses.SUCCEEDED,
  [RawPaymentStatuses.PENDING]: UiPaymentStatuses.PENDING,
  [RawPaymentStatuses.PROCESSING]: UiPaymentStatuses.PENDING,
  [RawPaymentStatuses.REFUNDED]: UiPaymentStatuses.REFUNDED,
};

export function getUiPaymentStatus(row: {
  status: string;
  amount?: number | null;
  amountRefunded?: number | null;
}): UiPaymentStatus {
  const amountRefunded = row.amountRefunded ?? 0;
  if (amountRefunded > 0 && amountRefunded >= (row.amount ?? 0)) {
    return UiPaymentStatuses.REFUNDED;
  }
  const status = (row.status || '').toLowerCase() as RawPaymentStatus;
  const uiStatus = RAW_TO_UI_STATUS[status] ?? UiPaymentStatuses.FAILED;
  if (amountRefunded > 0 && uiStatus === UiPaymentStatuses.SUCCEEDED) {
    return UiPaymentStatuses.PARTIALLY_REFUNDED;
  }
  return uiStatus;
}

export type DeclineInfo = {
  code: string | null;
  message: string | null;
};

export function getDeclineInfo(row: Payment): DeclineInfo | null {
  const uiStatus = getUiPaymentStatus(row);
  if (uiStatus === UiPaymentStatuses.SUCCEEDED || uiStatus === UiPaymentStatuses.PARTIALLY_REFUNDED)
    return null;
  const code = row.providerDeclineCode || null;
  const message = row.providerDeclineMessage || null;
  if (code || message) return { code, message };
  return null;
}

export function formatDeclineLine(info: DeclineInfo, isMobile?: boolean): string {
  if (!info.message) return '';
  const maxLength = isMobile ? 10 : 25;
  if (info.message.length <= maxLength) return `·${info.message}`;
  return `·${info.message.slice(0, maxLength)}…`;
}

function isProductionApi(): boolean {
  const apiUrl = import.meta.env.VITE_API_URL ?? '';
  return !apiUrl.includes('payments-api.nutrameg.dev');
}

export function getPrimerDashboardPaymentUrl(providerId: string): string {
  const base = isProductionApi()
    ? 'https://dashboard.primer.io'
    : 'https://sandbox-dashboard.primer.io';
  return `${base}/payments/${encodeURIComponent(providerId)}`;
}

function getYunoDashboardPaymentUrl(providerId: string): string {
  return `https://dashboard.y.uno/payments/details/${encodeURIComponent(providerId)}?isTesting=${!isProductionApi()}`;
}

export function getProviderDashboardPaymentUrl(provider: string, providerId: string): string {
  return provider === PaymentProviders.YUNO
    ? getYunoDashboardPaymentUrl(providerId)
    : getPrimerDashboardPaymentUrl(providerId);
}

export function getPaymentChargerFlowInfo(payment: Payment): { name: string; id: string } {
  const err = payment.paymentProviderError;
  if (!err) return { name: '', id: '' };
  const name = (err.chargerFlow?.name ?? '').toString().trim();
  const rawId = err.chargerFlowId ?? err.chargerFlow?.id ?? '';
  const id = typeof rawId === 'string' ? rawId.trim() : String(rawId ?? '').trim();
  return { name, id };
}

export function getPaymentSubscriptionLabel(row: Payment): string {
  const session = row.paymentSession;
  const offerType = session?.offer?.type?.trim();
  if (offerType) return offerType;
  const features = session?.priceProductObject?.features ?? [];
  return features.join(', ');
}

export function getPaymentMethodMaskedDigits(row: Payment): string {
  const method = row.customerPaymentMethod;
  const lastDigits = method?.lastDigits || method?.details?.paymentMethodData?.last4Digits;
  return formatMaskedCardDigits(lastDigits, { mask: '****' });
}

export function getPaymentCustomer(row: Payment): {
  name: string;
  email: string;
  externalId: string;
  brandCode?: string;
} {
  const customer = row.paymentSession?.customer;
  if (!customer) return { name: '', email: '', externalId: '' };
  const name =
    [customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.email || '';
  return {
    name,
    email: customer.email || '',
    externalId: customer.externalId || '',
    brandCode: row.brand?.brandCode,
  };
}

export function getCustomerProfileUrl(customer: {
  externalId: string;
  email: string;
  brandCode?: string;
}): string {
  if (!customer.externalId || !customer.brandCode) return '';

  let dashboardUrl = '';

  const productionApi = isProductionApi();

  switch (customer.brandCode) {
    case BrandCodes.KBM:
    case BrandCodes.FBM:
    case BrandCodes.SBM:
    case BrandCodes.BM:
    case BrandCodes.NM:
      dashboardUrl = productionApi
        ? 'https://admin-nnp-production.nutrameg.dev'
        : 'https://admin-nnp.nutrameg.dev';
      break;
    case BrandCodes.KG:
      dashboardUrl = 'https://admin.ketogo.app/';
      break;
    case BrandCodes.CF:
      dashboardUrl = productionApi
        ? 'https://admin.certifiedfasting.com/'
        : 'https://cf-admin-staging.nutrameg.dev/';
      break;
    case BrandCodes.CKD:
      dashboardUrl = 'https://admin.certifiedketodiet.com/';
      break;
    case BrandCodes.YOUTH_HG:
      dashboardUrl = 'https://admin.healthgenom.com/';
      break;
    case BrandCodes.MF:
      dashboardUrl = 'https://admin.monacofit.com/';
      break;
    case BrandCodes.SD:
      dashboardUrl = 'https://admin.stockholmdiet.com/';
      break;

    default:
      dashboardUrl = '';
  }

  return `${dashboardUrl}/customers/users/${encodeURIComponent(customer.externalId)}`;
}
